import { useState } from 'react'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Spinner } from '~/shadcn/ui/spinner'
import { Dialog, DialogContent } from '~/shadcn/ui/dialog'
import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { MainAgreementPreview } from '../main-agreement-preview/MainAgreementPreview'
import { useMainAgreement } from './useMainAgreement'
import { Button } from '~/shadcn/ui/button/button'
import { useForm } from 'react-hook-form'
import {
  unsophisticatedInvestorAnalysisFormSchema,
  type UnsophisticatedInvestorAnalysisFormSchema,
} from './unsophisticatedInvestorAnalysisFormSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import { Text } from '~/@mid_ui/typography/Text'
import { cn } from '~/shadcn/lib/utils'

type Props = { isSigningAgreement: boolean; onSign(): void }

function UnsophisticatedAgreementForm({ isSigningAgreement, onSign }: Props) {
  const {
    agreement,
    error: agreementLoadError,
    isLoading: isLoadingMainAgreement,
  } = useMainAgreement()

  const form = useForm<UnsophisticatedInvestorAnalysisFormSchema>({
    resolver: zodResolver(unsophisticatedInvestorAnalysisFormSchema),
    defaultValues: {
      isAgreedToTermsChecked: false,
    },
  })

  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false)

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSign)}>
          <FieldGroup>
            <FormField
              name="isAgreedToTermsChecked"
              control={form.control}
              render={({ field, fieldState }) => (
                <FormItem className="gap-1">
                  <FieldLabel className="flex flex-row items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSigningAgreement}
                    />
                    <Text className="text-sm font-medium leading-normal">
                      Súhlasím s podmienkami{' '}
                      <Button
                        variant="link"
                        size="xs"
                        className="text-accent p-0 h-1"
                        onClick={(event) => {
                          event.preventDefault()
                          setIsAgreementModalOpen(true)
                        }}>
                        rámcovej zmluvy
                      </Button>{' '}
                      o investovaní na platforme Invest in Slovakia.
                    </Text>
                  </FieldLabel>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />
            <Field>
              <Button
                variant="accent"
                type="submit"
                disabled={
                  isSigningAgreement ||
                  isLoadingMainAgreement ||
                  agreement == null
                }
                size="2xl">
                Potvrdiť a podpísať{' '}
                {isSigningAgreement ? (
                  <Spinner className="size-6" />
                ) : (
                  <ArrowForwardIcon />
                )}
              </Button>
            </Field>
          </FieldGroup>
        </form>
      </Form>

      <Dialog
        open={isAgreementModalOpen}
        onOpenChange={(isOpen) => setIsAgreementModalOpen(isOpen)}>
        <DialogContent
          className={cn(
            'flex flex-col max-w-[min(42rem,calc(100%-2rem))] h-[calc(100svh-4rem)] md:py-0 md:px-0',
            'text-[13px] font-semibold text-muted-foreground'
          )}>
          <MainAgreementPreview
            agreement={agreement}
            error={agreementLoadError}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export { UnsophisticatedAgreementForm }
